import { umbra_getMessageReactions } from "@/server/services/umbra/reactionService";
import { useInfiniteQuery } from "@tanstack/react-query";

interface UsePaginatedReactionsOptions {
  chatId: string;
  messageId: string;
  emoji?: string;
}

export function usePaginatedMessageReactions({
  chatId,
  messageId,
  emoji,
}: UsePaginatedReactionsOptions) {
  const {
    data,
    status,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["message-reactions", chatId, messageId, emoji],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const response = await umbra_getMessageReactions({
        chatId,
        messageId,
        emoji,
        cursor: pageParam ?? undefined,
      });
      return response;
    },
    getNextPageParam: (lastPage) => {
      // get the cursor if it exists, if not => undefined (no more pages)
      return lastPage.cursor ?? undefined;
    },
    initialPageParam: null, // first there is no cursor
    // enabled: !!emoji,
    refetchOnReconnect: true,
    // TO DO => To be configured later based on estimated usage and best performance
    // staleTime: 60000,
    // gcTime: 1000, // Inactive queries are garbage collected after 5 minutes by default.
    // refetchInterval: 10000,
    // refetchOnWindowFocus: true,
  });

  // Helper to check if we're fetching all reactions or a specific one
  const isFetchingAllReactions = !emoji;

  return {
    data, // Raw pages
    status,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
    refetch,
    isFetchingAllReactions,
  };
}
